import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Input, Button, Typography, Tag, Avatar, Tooltip, Space } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
  UploadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { FiSearch } from "react-icons/fi";
import {
  Wrapper,
  TopSection,
  TopTextWrapper,
  StyledHeader,
  StyledTable,
} from "../videos/styled";
import script from "../../../../services/script";
import { capitalizeFirstLetter } from "../../../../utils/utils";
import moment from "moment";
import AssignScriptModal from "./AssignScriptModal";
import ViewScriptDetails from "./ViewScriptDetails";
import RecordingByScript, { ExpandedTableWrapper } from "./RecordingByScript";
import { CustomButton, CustomSubTitle, CustomTitle } from "../../physician_page/styled";
import { debounce, notify } from "../../../../utils/helper";
import recording from "../../../../services/recording";
import { extractToken } from "../../../../utils/localStorage";
import UploadModal from "./UploadModal";
import { globalRoles } from "../../../../utils/roles";
import { physicianService } from "../../../../services/user";
import TermAndCondition from "../../../record/TermAndCondition";
import ScriptEditor from "./ScriptEditor";
import { CustomSearchContainer, CustomExpandSearchContainer } from "../../../table-item";
import DeleteConfirmationModal from "../../physician_page/component/DeleteConfirmationModal";
import Filter from "../../../table-item/filter/Filter";

const { Title, Text } = Typography;

const statusOptions = ["pending", "approved", "rejected"]
//   { label: "All", value: "All" },
//   { label: "Pending", value: "pending" },
//   { label: "Approved", value: "approved" },
//   { label: "Rejected", value: "rejected" },
// ];

const ScriptLibraryPage = () => {
  const user = extractToken();
  const isOwner = user?.role === globalRoles.SUPERADMIN;

  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [allScript, setAllScript] = useState([]);
  const [count, setCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedScriptId, setSelectedScriptId] = useState(null);
  const [scriptDataById, setScriptDataById] = useState([]);
  const [sortingColumn, setSortingColumn] = useState("");
  const [isUploadModalOpen, setisUploadModalOpen] = useState(false);
  const [consentAcceptData, setConsentAcceptData] = useState([]);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  const [isCreatingScript, setIsCreatingScript] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Audio player states
  const [recordingFileUrl, setRecordingFileUrl] = useState("");
  const [recordingFileName, setRecordingFileName] = useState("");
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [filterTab, setFilterTab] = useState([]);
  const [scriptEdit, setScriptEdit] = useState([]);

  // Function to get recording file from S3
  const getRecordingFileFromS3 = async (fileName) => {
    try {
      // Add your S3 file retrieval logic here
      const response = await recording.getRecordingFileFromS3({ fileName });
      setRecordingFileUrl(response.data.fileUrl);
      setRecordingFileName(fileName);
    } catch (error) {
      console.error("Error getting recording file:", error);
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "scriptTitle",
      key: "scriptTitle",
      width: 250,
      sorter: (a, b) => (a.scriptTitle || "").localeCompare(b.scriptTitle || ""),
      render: (scriptTitle, record) => (
        <div
          onClick={() => {
            setSelectedScriptId(record?.id);
            setViewModalOpen(true);
          }}
          className="cursor d-flex flex-column justify-start align-start"
        >
          <span style={{width: "-webkit-fill-available"}} className="ellipsis">
            {capitalizeFirstLetter(scriptTitle)}
          </span>
          <span 
            style={{width: "-webkit-fill-available"}} 
            className="ellipsis">
              {capitalizeFirstLetter(record.scriptContent)}
          </span>
        </div>
      ),
    },
   
    {
      title: "Assigned To",
      dataIndex: "physicianId",
      key: "physicianId",
      align: "center",
      width: 250,
      ellipsis: true,
      sorter: (a, b) => a.approvedBy?.localeCompare(b.approvedBy),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 100,
      sorter: (a, b) => a.status?.localeCompare(b.status),
      render: (status) => {
        let color = "default";
        const lowerCaseStatus = status?.toLowerCase();
        if (lowerCaseStatus === "draft") {
          color = "gray";
        } else if (lowerCaseStatus === "pending") {
          color = "orange";
        } else if (lowerCaseStatus === "approved") {
          color = "green";
        } else if (lowerCaseStatus === "rejected") {
          color = "red";
        }
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      width: 180,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      align: "center",
      key: "updatedAt",
      width: 180,
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
    },
    {
      title: "Actions",
      align: "center",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <div
              onClick={() => {
                setScriptEdit(record)
                setIsCreatingScript(true);
              }}
              className="icon-button-edit"
            >
              <EditOutlined />
            </div>
          </Tooltip>
          <Tooltip title="Delete">
            <div
              onClick={() => {
                setSelectedScriptId(record?.id);
                setIsDeleteModalOpen(true);
              }}
              className="icon-button-delete"
            >
              <DeleteOutlined />
            </div>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const fetchScript = async (searchInput = searchText) => {
    try {
      let statusFilter = filterTab === "All" ? "" : filterTab;
      setLoading(true);
      const res = await script.getScripts(
        pagination?.pageSize,
        pagination?.current,
        searchInput,
        sortingColumn,
        statusFilter
      );

      const processedData = processData(res?.data?.results || []);
      setAllScript(processedData);
      setCount(res?.data?.totalResults);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const fetchScriptById = async () => {
    try {
      setLoading(true);
      const res = await script.getScript(selectedScriptId);
      setScriptDataById(res?.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedScriptId) {
      fetchScriptById();
    }
  }, [selectedScriptId]);

  const processData = (data) => {
    return data.map((item) => ({
      ...item,
      physicianId: (
        <>
        {item?.boardMember?.name ? (
         <div className="row-flex">
          <div className="d-flex justify-content-center">
            <Avatar
              size={40}
              style={{
                marginRight: "8px",
                backgroundColor: "var(--border-color)",
                color: "var(--primary-text-color)",
              }}
            >
              {capitalizeFirstLetter(item?.boardMember?.name?.[0])}
            </Avatar>
          </div>
          <div className="d-flex flex-column justify-start align-start">
            <span>{capitalizeFirstLetter(item?.boardMember?.name)}</span>
            <span>{capitalizeFirstLetter(item?.boardMember?.email)}</span>
          </div>
        </div>
        ) : <span className="text-gray">-</span>}
        </>
      ),
      // description: capitalizeFirstLetter(item.scriptContent),
      createdAt: moment(item?.createdAt).format("DD-MMM-YYYY hh:mm A"),
      updatedAt:
        moment(item?.updatedAt).format("DD-MMM-YYYY hh:mm A") || item.createdAt,
      key: item.id,
      approvedBy: item?.boardMember?.name,
    }));
  };

  useEffect(() => {
    fetchScript();
  }, [pagination, sortingColumn, filterTab]);

  const handleSearch = useCallback(
    debounce((value) => {
      fetchScript(value);
    }, 500),
    []
  );

  const handleChangeTable = (a, b, c) => {
    const sortColumn = c?.column?.dataIndex;
    const sortOrder = c?.order;

    if (sortColumn && sortOrder) {
      const normalizedOrder = sortOrder === "ascend" ? "asc" : "desc";
      setSortingColumn(`${sortColumn}:${normalizedOrder}`);
    } else {
      setSortingColumn("");
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.key]);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.key));
    }
  };

  const expandedRowRender = (record) => {

    return (
      <div style={{ margin: 0, padding: "0 24px" }}>
        <Row className="d-flex" style={{justifyContent: "space-between", marginBottom: 8}}>
        <Typography.Title
          level={5}
          style={{ color: "var(--primary-text-color)" }}
        >
          Recordings for "{record.scriptTitle}"
        </Typography.Title>
        <CustomExpandSearchContainer style={{marginRight: "14px"}}>
          <FiSearch style={{position: "absolute", zIndex: 1}} className="icon ml-5" />
          <Input
           placeholder="Search here..."
           value={search}
           onChange={(e) => setSearch(e.target.value)}
           />
        </CustomExpandSearchContainer>
        </Row>
        <RecordingByScript
          scriptId={record.id}
          recordingFileUrl={recordingFileUrl}
          recordingFileName={recordingFileName}
          getRecordingFileFromS3={getRecordingFileFromS3}
          search={search}
        />
      </div>
    );
  };

  const getMyProfile = async () => {
    try {
      setLoading(true);
      const res = await physicianService.getMyProfile();
      setConsentAcceptData(res?.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const handleDeleteScript = async () => {
    try {
      setDeleteLoading(true);
      await script.deleteScript(selectedScriptId);
      notify("success", "Script deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedScriptId(null);
      fetchScript(); // Refresh the script list
    } catch (error) {
      notify("error", "Failed to delete script");
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    getMyProfile();
  }, []);

  const data = allScript.length;

  return (
    <>
        <TopSection>
          <Row className="custom-header w-100" style={{marginBottom: "10px"}}>
            <Col>
                <TopTextWrapper>
                  <CustomTitle>Script Library</CustomTitle>
                  <CustomSubTitle>Manage and organize scripts.</CustomSubTitle>
                </TopTextWrapper>
            </Col>
            {!isCreatingScript && (
            <Col className="d-flex align-items-end pr-5">
               <CustomButton
                className="upload-button"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsCreatingScript(true)}
                >
                Create Script
              </CustomButton>
            </Col>
            )}
          </Row>
        </TopSection>

        {isCreatingScript ? (
          <ScriptEditor
          onSaveSuccess={() => {
            setIsCreatingScript(false);
            setScriptEdit([]);
            fetchScript();
          }}
          onCancel={() => {
            setIsCreatingScript(false)
            setScriptEdit([]);
          }}
          scriptToEdit={scriptEdit}
        />
        ): (
          <Wrapper
            gutter={[0, 32]}
            style={{
              borderRadius: "10px",
              border: "1px solid var(--border-color)",
              marginTop: "10px",
              marginBottom: "32px",
            }}
          >
            <Col span={24} style={{ padding: "0px 24px" }}>
              <StyledHeader>
                <Col
                  span={24}
                  className="d-flex"
                  style={{ justifyContent: "space-between" }}
                >
                  <div className="d-flex  gap-12" style={{ width: "100%" , justifyContent:'start'}}>
                  <CustomSearchContainer>
                    <FiSearch
                      style={{ position: "absolute", zIndex: 1 }}
                      className="icon ml-5"
                    />
                    <Input
                      // allowClear
                      className="search-bar"
                      placeholder="Search scripts by title..."
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        handleSearch(e.target.value);
                      }}
                      value={searchText}
                    />
                    </CustomSearchContainer>
                    <Filter
                  show={show}
                  setShow={setShow}
                  data={[
                     
                    {
                      title: "Status",
                      list: statusOptions,
                      filterList: filterTab,
                      setFilterList: setFilterTab,
                      tagColors: {
                        // "All": { color: "default", label: "All" },
                        "pending": { color: "orange", label: "PENDING" },
                        "approved": { color: "green", label: "APPROVED" },
                        "rejected": { color: "red", label: "REJECTED" }
                      }
                    },

                   
                   
                  ]}
                />
                  </div>
                  <div className="d-flex gap-12">
                    {/* {selectedRowKeys.length > 0 && (
                      <Button
                        className="upload-button"
                        type="primary"
                        onClick={() => setIsModalOpen(true)}
                      >
                        Assign Scripts
                      </Button>
                    )} */}
                    {isOwner && (
                      <Button
                        className="upload-button"
                        type="primary"
                        icon={<UploadOutlined size={22} />}
                        onClick={() => {
                          if (consentAcceptData?.consentAccept === false) {
                            setConsentModalOpen(true);
                            return;
                          } else {
                            setisUploadModalOpen(true);
                          }
                        }}
                      >
                        Upload
                      </Button>
                    )}
                  </div>
                </Col>
              </StyledHeader>
            </Col>

            <Col span={24} style={{ flex: 1, padding: "0px 24px" }}>
              {/* <ExpandedTableWrapper> */}
                <StyledTable
                  loading={loading}
                  columns={columns}
                  dataSource={allScript}
                  rowKey="key"
                  // rowSelection={rowSelection}
                  scroll={data?.length > 0 ? { y: `calc(100vh - 320px)`, x: true } : null}
                  expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpand: handleExpand,
                    expandIcon: ({ expanded, onExpand, record }) => (
                      <Button
                        type="text"
                        size="small"
                        icon={
                          expanded ? (
                            <DownOutlined className="ml-5 text-primary" />
                          ) : (
                            <RightOutlined className="ml-5 text-primary" />
                          )
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onExpand(record, e);
                        }}
                        style={{ border: "none", padding: 0 }}
                      />
                    ),
                  }}
                  pagination={{
                    current: pagination?.current,
                    pageSize: pagination?.pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: [25, 50, 100],
                    onShowSizeChange: (current, size) => {
                      setPagination({
                        ...pagination,
                        current,
                        pageSize: size,
                      });
                    },
                    total: count,
                    showTotal: (total, range) =>
                      `${range[0]} - ${range[1]} of ${total} scripts`,
                    showQuickJumper: false,
                    showLessItems: true,
                    itemRender: (page, type, originalElement) => {
                      if (type === "prev") {
                        return (
                          <Button
                            type="text"
                            disabled={pagination?.current === 1}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (pagination?.current > 1) {
                                setPagination((prev) => ({
                                  ...prev,
                                  current: prev.current - 1,
                                }));
                              }
                            }}
                          >
                            Previous
                          </Button>
                        );
                      }
                      if (type === "next") {
                        const maxPage = Math.ceil(count / pagination?.pageSize);
                        return (
                          <Button
                            type="text"
                            disabled={pagination?.current === maxPage}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (pagination?.current < maxPage) {
                                setPagination((prev) => ({
                                  ...prev,
                                  current: prev.current + 1,
                                }));
                              }
                            }}
                          >
                            Next
                          </Button>
                        );
                      }
                      return originalElement;
                    },
                  }}
                  onChange={handleChangeTable}
                />
              {/* </ExpandedTableWrapper> */}
            </Col>
          </Wrapper>
          )}

          {isModalOpen && (
            <AssignScriptModal
              isOpen={isModalOpen}
              setOpen={setIsModalOpen}
              selectedRowKeys={selectedRowKeys}
              setSelectedRowKeys={setSelectedRowKeys}
            />
          )}
          {viewModalOpen && (
            <ViewScriptDetails
              isOpen={viewModalOpen}
              setOpen={setViewModalOpen}
              title={"Script Details"}
              subtitle={"View the details of the script"}
              DataById={scriptDataById}
            />
          )}
          {isUploadModalOpen && (
            <UploadModal
              isOpen={isUploadModalOpen}
              setOpen={setisUploadModalOpen}
              fetchScript={fetchScript}
            />
          )}
          {consentModalOpen && (
            <TermAndCondition
              isOpen={consentModalOpen}
              setOpen={setConsentModalOpen}
              profileData={consentAcceptData}
              getMyProfile={getMyProfile}
            />
          )}
          {isDeleteModalOpen && (
            <DeleteConfirmationModal
              isOpen={isDeleteModalOpen}
              setOpen={setIsDeleteModalOpen}
              onConfiem={handleDeleteScript}
              loading={deleteLoading}
              title="Delete Script"
              message="Are you sure you want to delete this script?"
            />
          )}
    </>
  );
};

export default ScriptLibraryPage;
